/* `/skills` — the skills catalog, one skill, one of its files, and the skills on an agent.
   backend/modules/skills/README.md for the wire.

   In `shared/api/` because two features read it: the Skills page browses the catalog,
   an agent's Settings puts skills on the agent (rule book §1 rule 5).

   Paths are bare; `client.baseURL` carries `/api/v1`. Every enveloped response goes
   through `unwrap`; a file is the one that is not. */
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { agentKeys } from "./agents";
import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { AgentSkill, SkillDetail, SkillLink, SkillsPage, SkillsQuery } from "@/shared/types";

export const skillsKeys = {
  all: ["skills"] as const,
  list: (params: Omit<SkillsQuery, "offset">) => [...skillsKeys.all, "list", params] as const,
  detail: (id: string) => [...skillsKeys.all, "detail", id] as const,
  /** The detail and every file's bytes, as the editor opens them. */
  toEdit: (id: string) => [...skillsKeys.detail(id), "to-edit"] as const,
  /** A file as the skill has it now, or as an agent's link holds it (`agentId`). */
  file: (id: string, path: string, agentId?: string) => [...skillsKeys.all, "file", id, path, agentId ?? null] as const,
  agent: (agentId: string) => [...skillsKeys.all, "agent", agentId] as const,
};

/* A workspace's own skills arrive one ticket at a time (tasks/custom-skills). Until a way
   in has its route, its control stays off and says it isn't available yet, rather than
   failing on a real account (frontend/README.md, "A screen with no backend yet").
   The ticket that ships the route turns its line on. Mock mode turns every one on: MSW
   answers them all. */
const SKILL_ACTIONS_LIVE = {
  workspaceTab: true, // GET /skills?source=workspace (DEF-277)
  upload: true, // POST /skills/check, POST /skills (DEF-277)
  write: true, // the editor's save (DEF-278)
  edit: true, // PUT /skills/{id} (DEF-279)
  delete: true, // DELETE /skills/{id} (DEF-279)
  takeUpdate: true, // POST /skills/{id}/agents/{agent_id}/update (DEF-279)
  askDefinable: true, // Definable's create_skill, which saves what it drafted (DEF-280)
  github: true, // POST /skills/github (DEF-281)
};

export type SkillAction = keyof typeof SKILL_ACTIONS_LIVE;

export const isSkillActionLive = (action: SkillAction): boolean =>
  process.env.REACT_APP_API_MOCK === "true" || SKILL_ACTIONS_LIVE[action];

const PAGE_SIZE = 60;

// shortcut: one page of the most the API serves; an agent holding more skills is not a case yet
const AGENT_SKILLS_LIMIT = 200;

/* ------------------------------ endpoints -------------------------------- */

export async function listSkills(params: SkillsQuery = {}): Promise<SkillsPage> {
  return unwrap(await client.get<Envelope<SkillsPage>>("/skills", { params }));
}

export async function getSkill(id: string): Promise<SkillDetail> {
  return unwrap(await client.get<Envelope<SkillDetail>>(`/skills/${id}`));
}

/** One file's bytes, unenveloped, and the version they are of (`X-Skill-Version`): the one a
    yes over what is on screen names. The server sends text as `text/plain` whatever it holds
    and only raster images as images — never anything a browser would run. Each path segment
    is encoded; the slashes between them stay. `agentId`: the file as that agent's link holds
    it — the version a request asks for — not the skill's newest. */
export async function getSkillFile(id: string, path: string, agentId?: string): Promise<{ blob: Blob; version: string | null }> {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  const params = agentId ? { agent_id: agentId } : undefined;
  const res = await client.get<Blob>(`/skills/${id}/files/${encoded}`, { responseType: "blob", params });
  return { blob: res.data, version: res.headers["x-skill-version"] ?? null };
}

/** The skills on an agent, requested or attached, each card with its `link`. */
export async function listAgentSkills(agentId: string): Promise<AgentSkill[]> {
  const page = unwrap(
    await client.get<Envelope<SkillsPage<AgentSkill>>>("/skills", { params: { agent_id: agentId, limit: AGENT_SKILLS_LIMIT } }),
  );
  return page.items;
}

/** The agent's owner attaches it; anyone else who can edit the agent asks the owner — for the
    skill's version they read, when they say which (409 3170016 once it moved on). */
export async function attachSkill(skillId: string, agentId: string, version?: string): Promise<SkillLink> {
  return unwrap(await client.post<Envelope<SkillLink>>(`/skills/${skillId}/agents`, { agent_id: agentId, ...(version ? { version } : {}) }));
}

/** The owner's yes to a request — for the version they read (the link's); a newer one that
    came in meanwhile is refused (409 3170016). */
export async function approveSkill(skillId: string, agentId: string, version?: string): Promise<SkillLink> {
  return unwrap(await client.post<Envelope<SkillLink>>(`/skills/${skillId}/agents/${agentId}/approve`, version ? { version } : {}));
}

/** The owner takes the skill's newest version onto the agent — the one they read (the
    skill's `version`); 409 3170016 when it changed again meanwhile. */
export async function updateSkillLink(skillId: string, agentId: string, version?: string): Promise<SkillLink> {
  return unwrap(await client.post<Envelope<SkillLink>>(`/skills/${skillId}/agents/${agentId}/update`, version ? { version } : {}));
}

/** Takes it off the agent, or turns the request down. */
export async function removeSkill(skillId: string, agentId: string): Promise<void> {
  await client.delete(`/skills/${skillId}/agents/${agentId}`);
}

/* -------------------------------- hooks ---------------------------------- */

/** The catalog, a page at a time: `fetchNextPage` is "Show more". Holds the last
    result while a new search loads, so typing does not blank the list. */
export function useSkills(params: Omit<SkillsQuery, "limit" | "offset">) {
  return useInfiniteQuery({
    queryKey: skillsKeys.list(params),
    queryFn: ({ pageParam }) => listSkills({ ...params, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.items.length;
      return next < last.total ? next : undefined;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSkill(id: string) {
  return useQuery({ queryKey: skillsKeys.detail(id), queryFn: () => getSkill(id) });
}

/** One file, with its text when it is text, and the version it is of. `null` path = nothing
    open. Silent: the Skills page's viewer draws a failure itself (a gone file is a normal
    state there). */
export function useSkillFile(id: string, path: string | null, agentId?: string) {
  return useQuery({
    queryKey: skillsKeys.file(id, path ?? "", agentId),
    queryFn: async () => {
      const { blob, version } = await getSkillFile(id, path as string, agentId);
      return { blob, version, text: blob.type.startsWith("text/") ? await blob.text() : null };
    },
    enabled: path !== null,
    meta: { silent: true },
  });
}

export function useAgentSkills(agentId: string) {
  return useQuery({
    queryKey: skillsKeys.agent(agentId),
    queryFn: () => listAgentSkills(agentId),
    enabled: !!agentId,
  });
}

/* Every link write re-reads the agent's skills and the agents: an attached skill is on
   the draft, so adding or removing one changes `has_unpublished_changes`. */
function useLinkWrite<T>(write: (skillId: string, agentId: string, version?: string) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skillId, agentId, version }: { skillId: string; agentId: string; version?: string }) =>
      write(skillId, agentId, version),
    onSettled: () => Promise.all([
      qc.invalidateQueries({ queryKey: skillsKeys.all }),
      qc.invalidateQueries({ queryKey: agentKeys.all }),
    ]),
  });
}

export const useAttachSkill = () => useLinkWrite(attachSkill);
export const useApproveSkill = () => useLinkWrite(approveSkill);
export const useRemoveSkill = () => useLinkWrite(removeSkill);
export const useUpdateSkillLink = () => useLinkWrite(updateSkillLink);
